/**
 * IFC file -> Fragments model.
 *
 * WASM is self-hosted with `autoSetWasm: false`. The default (`true`) silently
 * fetches web-ifc from unpkg at runtime, which we refuse to ship — supply-chain
 * risk plus an availability dependency. See CODE_RULES.md §7.
 *
 * API verified against @thatopen/components 3.4.8 type definitions.
 */
import type * as OBC from "@thatopen/components";
import type * as FRAGS from "@thatopen/fragments";
import { ASSETS } from "../core/config";
import { appError, describeUnknown, err, ok, type Result } from "../core/result";
import { logger } from "../core/logger";

export type IfcProgress = (fraction: number) => void;

export async function setupIfcLoader(loader: OBC.IfcLoader): Promise<void> {
  await loader.setup({
    autoSetWasm: false,
    wasm: { path: ASSETS.WASM_DIR, absolute: true },
  });
}

/**
 * Converts validated IFC bytes into a Fragments model. The model is added to
 * the scene by the `onItemSet` handler wired in fragments-engine.ts.
 *
 * Conversion is the expensive step (WASM, single-threaded). A large model can
 * occupy the main thread for tens of seconds; callers must show progress.
 */
export async function loadIfc(
  loader: OBC.IfcLoader,
  bytes: Uint8Array,
  fileName: string,
  onProgress: IfcProgress,
): Promise<Result<FRAGS.FragmentsModel>> {
  const startedAt = performance.now();
  try {
    const model = await loader.load(bytes, true, fileName, {
      processData: { progressCallback: onProgress },
    });
    logger.info("IFC loaded", {
      elapsedMs: Math.round(performance.now() - startedAt),
      bytes: bytes.byteLength,
    });
    return ok(model);
  } catch (cause) {
    logger.error("IFC parse failed", cause);
    return err(
      appError(
        "IFC_PARSE_FAILED",
        "That IFC file could not be read. It may be corrupt or use an unsupported schema.",
        describeUnknown(cause),
      ),
    );
  }
}
